import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import color from "../../Constants/colors";
import { Features, UserRole } from "../../Enums/companyStatus";
import { hasPermission, hasRole } from "../../Helpers/authHelpers";
import { ActivitySource } from "../../Models/dashboard";
import { VOICES_FEATURES } from "../Voices/Navigation/voicesNavigation";
import ActivityCard from "./ActivityCard";
import AiPartnersSection from "./AiPartnersSection";
import GlobalSearchDialog from "./GlobalSearchDialog";
import HomeHero from "./HomeHero";
import ModulesSection from "./ModulesSection";
import RecentItemsCard from "./RecentItemsCard";
import ReleaseNotesCard from "./ReleaseNotesCard";
import UpcomingTasksCard from "./UpcomingTasksCard";
import WorkspaceCard from "./WorkspaceCard";
import { useDashboardModules } from "./dashboardModules";

const HomePage: React.FC = () => {
  const [searchOpen, setSearchOpen] = useState(false);
  const modules = useDashboardModules();
  const isAdmin = hasRole(UserRole.ADMIN);
  const can = (feature: Features) => isAdmin || hasPermission(feature);

  const activitySources: ActivitySource[] = [
    ...(can(Features.COMO_EXPERTS) ? (["chat"] as const) : []),
    ...(can(Features.EVALUATION) ? (["evaluation"] as const) : []),
    ...(isAdmin || VOICES_FEATURES.some(hasPermission)
      ? (["voices"] as const)
      : []),
    ...(can(Features.DOCUMENT_CRAFTING)
      ? (["document_crafting"] as const)
      : []),
  ];

  // Ctrl/Cmd+K opens search from anywhere on the page.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <Box sx={{ minHeight: "100%", backgroundColor: color.gray50 }}>
      <Box
        sx={{
          maxWidth: 1440,
          mx: "auto",
          px: { xs: 2, md: 3 },
          py: { xs: 2, md: 3 },
        }}
      >
        <HomeHero onOpenSearch={() => setSearchOpen(true)} />

        <Box
          sx={{
            mt: 3,
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              lg: "minmax(0, 1fr) 360px",
            },
            gap: 3,
            alignItems: "start",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              minWidth: 0,
            }}
          >
            <ModulesSection modules={modules} />
            {activitySources.length > 0 && (
              <ActivityCard sources={activitySources} />
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              minWidth: 0,
            }}
          >
            <RecentItemsCard />
            {can(Features.COMO_EXPERTS) && <UpcomingTasksCard />}
            <ReleaseNotesCard />
            <WorkspaceCard />
          </Box>
        </Box>

        <Box sx={{ mt: 3 }}>
          <AiPartnersSection />
        </Box>
      </Box>

      <GlobalSearchDialog
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        modules={modules}
      />
    </Box>
  );
};

export default HomePage;
